import React from 'react'
import PropTypes from 'prop-types'
import { conditionPolarityLabels } from 'i18n/displayConstants/conditions'
import conditionLabelText from 'components/feature/utils/conditionLabelText'
import ReadOnlyParameterValue from 'components/feature/NavigatorPanes/editors/base/ReadOnlyParameterValue'
import {
  CAN_INPUT,
  CARRY_LOGIC_SET,
  CARRY_TIMER,
  ANALOG_INPUT,
  DIGITAL_INPUT,
} from 'utils/constants'
import strings from 'utils/strings'
import { edgeLabel } from 'components/feature/utils'
import EdgeImg from 'components/feature/EdgeImg'
import { EvaluationIconStyled } from 'components/patterns/Icon/IconsStyled'
import TimerSimulatedValueDisplay from 'components/feature/Simulation/TimerSimulatedValueDisplay'
import styled from '@emotion/styled'
import { themeColor } from 'utils/styles'
import ConditionSimulatorInput from './ConditionSimulatorInput'
import { ListLabel, ConditionRow, StyledImg } from './ConditionSimulator.styles'

const makeConditionInputTitle = (selectedInputDescription, inputConfig) =>
  `${selectedInputDescription}${
    conditionPolarityLabels[inputConfig]
      ? `, ${conditionPolarityLabels[inputConfig]}`
      : ''
  }`

const conditionSimulatorRenderStrategy = (rungType) => {
  if (rungType === CARRY_LOGIC_SET || rungType === CARRY_TIMER) {
    return null
  }
  return ConditionSimulatorInput
}

const ConditionSimulator = (props) => {
  const {
    inputUri,
    selectedInputDescription,
    operator,
    value,
    inputConfig,
    translations,
    translationType,
    units,
    rungType,
    result,
    queryType,
    edge,
    evaluationResult,
    edgeEvaluation,
    current,
    showEvaluation = true,
    customDescription,
    subType,
  } = props

  if (queryType !== CAN_INPUT && !inputUri) return null
  const Component = conditionSimulatorRenderStrategy(rungType)
  return (
    <StyledConditionSimulator>
      <ListLabel>
        {makeConditionInputTitle(selectedInputDescription, inputConfig)}
      </ListLabel>
      {subType === ANALOG_INPUT && (
        <ConditionRow>
          <ReadOnlyParameterValue
            label={strings.custom_ai_pin_name}
            value={customDescription || selectedInputDescription}
          />
        </ConditionRow>
      )}
      {subType === DIGITAL_INPUT && (
        <ReadOnlyParameterValue
          label={strings.custom_di_pin_name}
          value={customDescription || selectedInputDescription}
        />
      )}
      {subType === CAN_INPUT && (
        <ReadOnlyParameterValue
          label={strings.custom_can_input_name}
          value={customDescription || selectedInputDescription}
        />
      )}
      {subType !== DIGITAL_INPUT &&
        subType !== ANALOG_INPUT &&
        subType !== CAN_INPUT &&
        customDescription && (
          <ReadOnlyParameterValue
            label={strings.tem_custom_switch_name}
            value={customDescription}
          />
        )}
      <ConditionRow>
        {showEvaluation &&
          rungType !== CARRY_TIMER &&
          evaluationResult !== null &&
          evaluationResult !== undefined && (
            <EvaluationIconStyled
              className="eval-icon"
              evaluation={evaluationResult}
            />
          )}
        {showEvaluation &&
          conditionLabelText({
            operator,
            value,
            rungType,
            selectedInputDescription,
            translationType,
            inputConfig,
            translations,
            units,
            result,
            simulate: true,
          })}
        {rungType === CARRY_TIMER && (
          <TimerSimulatedValueDisplay
            className="timer-display"
            value={current}
            showHintText
          />
        )}
      </ConditionRow>
      {edge && (
        <ConditionRow>
          {edgeEvaluation !== null && edgeEvaluation !== undefined && (
            <EvaluationIconStyled
              className="edge-eval-icon"
              evaluation={edgeEvaluation}
            />
          )}
          <ReadOnlyParameterValue
            className="edge"
            label={strings.edge_detection}
            value={
              <>
                {edgeLabel(edge)}{' '}
                <StyledImg>
                  <EdgeImg edge={edge} />
                </StyledImg>
              </>
            }
          />
        </ConditionRow>
      )}
      {Component && <Component {...props} />}
    </StyledConditionSimulator>
  )
}

ConditionSimulator.propTypes = {
  inputUri: PropTypes.string.isRequired,
  selectedInputDescription: PropTypes.string,
  operator: PropTypes.number,
  value: PropTypes.number,
  inputConfig: PropTypes.string,
  translations: PropTypes.array,
  uri: PropTypes.string,
  dataType: PropTypes.string,
  translationType: PropTypes.string,
  maxValue: PropTypes.any,
  minValue: PropTypes.any,
  simulatedValue: PropTypes.any,
  units: PropTypes.string,
  rungType: PropTypes.string,
  queryType: PropTypes.string.isRequired,
  carryType: PropTypes.string,
  edge: PropTypes.string,
  edgeEvaluation: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.oneOf([null]),
  ]),
  result: PropTypes.bool,
  canReset: PropTypes.bool,
  evaluationResult: PropTypes.bool,
  showEvaluation: PropTypes.bool,
  handleUpdateSimulatedValue: PropTypes.func,
  resetSimulatedValue: PropTypes.func,
  current: PropTypes.any,
  customDescription: PropTypes.string,
  subType: PropTypes.string,
}

export default ConditionSimulator

export const StyledConditionSimulator = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid ${themeColor('greyPrimary')};

  &:last-of-type {
    border-bottom: 0;
  }

  &:only-child {
    border-bottom: 1px solid ${themeColor('greyPrimary')};
    border-top: 1px solid ${themeColor('greyPrimary')};
  }
`
